import React, { useState, useEffect } from 'react';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/shadcn-ui/select';
import { Button } from '@/components/shadcn-ui/button';
import { X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { appAxios } from '@/network/base/appAxios';
import { useUserStore } from '@/stores/useUser.store';
import { toast } from 'sonner';

const PermitFilters = ({ onFilterChange, onClose, className }) => {
    const [filters, setFilters] = useState({
        jurisdiction: 'all',
        permitType: 'all',
        status: 'all',
        automation: 'all',
        assignedTo: 'all',
    });
    const [prevFilters, setPrevFilters] = useState(filters);
    const [admins, setAdmins] = useState([]);
    const [loading, setLoading] = useState(false);
    const adminDetails = useUserStore(state => state.adminDetails);


    const fetchAdmins = async (page = 1) => {
        setLoading(true);
        try {
            const response = await appAxios.get(`/Admin/v1.0/admin-list?page=${page}&limit=10`);
            const filteredAdmins = response.data.data.data.filter(admin => admin.user_email !== adminDetails.user_email);
            setAdmins([{ _id: 'all', fullname: 'All Admins' }, ...filteredAdmins]);
        } catch (error) {
            toast.error('Failed to fetch admin list');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchAdmins();
    }, []);


    const jurisdictions = [
        { value: 'all', label: 'All Jurisdictions' },
        { value: 'ladbs', label: 'Los Angeles County' },
        { value: 'orange_county', label: 'Orange County' },
        { value: 'san_diego_county', label: 'San Diego County' },
        { value: 'riverside_county', label: 'Riverside County' },
    ];

    const permitTypes = [
        { value: 'all', label: 'All Types' },
        { value: '0', label: 'Bldg Alter Repair' },
        { value: '1', label: 'Electrical Vehicle Charger' },
        { value: '2', label: 'Electrical' },
        { value: '3', label: 'Fire Sprinkler' },
        { value: '4', label: 'HVAC' },
        { value: '5', label: 'Plumbing' },
        { value: '6', label: 'Pressure Vessel' },
        { value: '7', label: 'Temporary Special Event' },
    ];

    const statuses = [
        { value: 'all', label: 'All Statuses' },
        { value: 'new', label: 'New' },
        { value: 'in_review', label: 'In Review' },
        { value: 'added_to_process_queue', label: 'Added to Process Queue' },
        { value: 'added_to_signup_queue', label: 'Added to Signup Queue' },
        { value: 'added_to_profile_queue', label: 'Added to Profile Queue' },
        { value: 'portal_signup_failed', label: 'Portal Signup Failed' },
        // { value: 'signup_automation_started', label: 'Signup Automation Started' },
        // { value: 'profile_automation_started', label: 'Profile Automation Started' },
        { value: 'portal_profile_failed', label: 'Portal Profile Failed' },
        // { value: 'automation_started', label: 'Automation Started' },
        { value: 'automation_failed', label: 'Automation Failed' },
        { value: 'pending_payment', label: 'Pending Payment' },
        { value: 'portal_fee_payment_pending', label: 'Portal Fee Payment Pending' },
        { value: 'payment_received', label: 'Payment Received' },
        { value: 'permit_processing', label: 'Permit Processing' },
        { value: 'permit_ready', label: 'Permit Ready' },
        { value: 'completed', label: 'Completed' },
        { value: 'on_hold', label: 'On Hold' },
        { value: 'rejected', label: 'Rejected' },
    ];
    const automationStatuses = [
        { value: 'all', label: 'All' },
        { value: 'pending', label: 'Pending' },
        { value: 'running', label: 'Running' },
        { value: 'completed', label: 'Completed' },
        { value: 'failed', label: 'Failed' },
    ];

    const agents = [
        { value: 'all', label: 'All Agents' },
        { value: 'unassigned', label: 'Unassigned' },
        { value: 'agent_1', label: 'John Doe' },
        { value: 'agent_2', label: 'Jane Smith' },
        { value: 'agent_3', label: 'Mike Johnson' },
    ];
    const handleFilterChange = (key, value) => {
        setFilters(prev => ({
            ...prev,
            [key]: value,
        }));
    };

    const handleClearFilters = () => {
        const clearedFilters = {
            jurisdiction: 'all',
            permitType: 'all',
            status: 'all',
            automation: 'all',
            assignedTo: 'all',
        };
        setFilters(clearedFilters);
        setPrevFilters(clearedFilters);

        // Normalize and call parent immediately
        const normalizedFilters = {
            jurisdiction: '',
            permitType: '',
            status: '',
            automation: '',
            assignedTo: '',
        };
        onFilterChange(normalizedFilters);
    };

    const hasActiveFilters = Object.values(filters).some(value => value !== 'all');

    useEffect(() => {
        // Check if filters actually changed
        const filtersChanged = Object.keys(filters).some(
            key => filters[key] !== prevFilters[key]
        );

        if (!filtersChanged) return;

        // Debounce the filter changes
        const timer = setTimeout(() => {
            // Normalize filters - convert 'all' to empty string for API
            const normalizedFilters = Object.entries(filters).reduce((acc, [key, value]) => {
                acc[key] = value === 'all' ? '' : value;
                return acc;
            }, {});

            onFilterChange(normalizedFilters);
            setPrevFilters(filters);
        }, 300);

        return () => clearTimeout(timer);
    }, [filters]);

    if (loading) return <div className="flex items-center justify-center">Loading...</div>

    return (
        <div className={cn('bg-background border rounded-lg p-4 space-y-4', className)}>
            {/* Header */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <h3 className="font-semibold text-sm">Filter Permits</h3>
                    {hasActiveFilters && (
                        <span className="text-xs text-muted-foreground">
                            ({Object.values(filters).filter(v => v).length} active)
                        </span>
                    )}
                </div>
                <div className="flex items-center gap-2">
                    {hasActiveFilters && (
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={handleClearFilters}
                            className="h-8 text-xs"
                        >
                            Clear Filters
                        </Button>
                    )}
                    {onClose && (
                        <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => { handleClearFilters(); onClose() }}
                            className="h-8 w-8"
                        >
                            <X className="h-4 w-4" />
                        </Button>
                    )}
                </div>
            </div>

            {/* Filter Grid */}
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                {/* Jurisdiction */}
                <div className="space-y-2">
                    <label className="text-sm font-medium">Jurisdiction</label>
                    <Select
                        value={filters.jurisdiction}
                        onValueChange={(value) => handleFilterChange('jurisdiction', value)}
                    >
                        <SelectTrigger>
                            <SelectValue placeholder="All Jurisdictions" />
                        </SelectTrigger>
                        <SelectContent>
                            {jurisdictions.map((option) => (
                                <SelectItem key={option.value} value={option.value}>
                                    {option.label}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>

                {/* Permit Type */}
                <div className="space-y-2">
                    <label className="text-sm font-medium">Permit Type</label>
                    <Select
                        value={filters.permitType}
                        onValueChange={(value) => handleFilterChange('permitType', value)}
                    >
                        <SelectTrigger>
                            <SelectValue placeholder="All Types" />
                        </SelectTrigger>
                        <SelectContent>
                            {permitTypes.map((option) => (
                                <SelectItem key={option.value} value={option.value}>
                                    {option.label}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>

                {/* Status */}
                <div className="space-y-2">
                    <label className="text-sm font-medium">Status</label>
                    <Select
                        value={filters.status}
                        onValueChange={(value) => handleFilterChange('status', value)}
                    >
                        <SelectTrigger>
                            <SelectValue placeholder="All Statuses" />
                        </SelectTrigger>
                        <SelectContent>
                            {statuses.map((option) => (
                                <SelectItem key={option.value} value={option.value}>
                                    {option.label}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>

                {/* Automation */}
                <div className="space-y-2">
                    <label className="text-sm font-medium">Automation</label>
                    <Select
                        value={filters.automation}
                        onValueChange={(value) => handleFilterChange('automation', value)}
                    >
                        <SelectTrigger>
                            <SelectValue placeholder="All" />
                        </SelectTrigger>
                        <SelectContent>
                            {automationStatuses.map((option) => (
                                <SelectItem key={option.value} value={option.value}>
                                    {option.label}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>

                {/* Assigned To */}
                <div className="space-y-2">
                    <label className="text-sm font-medium">Assigned To</label>
                    <Select
                        value={filters.assignedTo}
                        onValueChange={(value) => handleFilterChange('assignedTo', value)}
                    >
                        <SelectTrigger>
                            <SelectValue placeholder="All Agents" />
                        </SelectTrigger>
                        <SelectContent>
                            {admins.map((option) => (
                                <SelectItem key={option._id} value={option._id}>
                                    {option.fullname}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
            </div>
        </div>
    );
};

export default PermitFilters;